import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import { initSentry } from './lib/sentry';
import { initAnalytics } from './lib/analytics';
import { readConsent } from './lib/consent';
import { initConsentBlockerProbe } from './lib/consentBlockerProbe';
import { router } from './router';
import './index.css';

// Watch for a cookie-consent blocker's pre-hide style as early as possible; it
// is removed within ~2s. Checkout uses this to keep MobiusPay's card fields
// styled when such an extension is active. No-op on ordinary browsers.
initConsentBlockerProbe();

// Error monitoring first. No-op without a DSN.
initSentry();
// GA4. No-op without a measurement id.
// Analytics only after the visitor accepted the cookie notice (CookieConsent).
if (readConsent() === 'accepted') initAnalytics();

// A chunk that will not load is almost always a stale tab opened before a
// deploy. Reload once to pick up the current index.html; once only, so a
// genuinely broken build cannot loop.
const RELOAD_FLAG = 'bs:chunk-reloaded';
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(RELOAD_FLAG)) return;
  event.preventDefault();
  sessionStorage.setItem(RELOAD_FLAG, '1');
  window.location.reload();
});
window.addEventListener('load', () => sessionStorage.removeItem(RELOAD_FLAG));

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
